import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  AiAgentCategory,
  AiAgentConfigDto,
  AiModelPricingDto,
  AiUsageSummary,
  AiUsageTimeseries,
  AiUsageCallRow,
  AiUsageGroupBy,
  UpdateAiAgentConfigInput,
  UpsertAiModelPricingInput,
} from '@respectly/shared-types';

/** 'all' plus every real category — derived so a new category cannot be forgotten here. */
export type CategoryFilter = 'all' | AiAgentCategory;

export interface AiUsageFilters {
  from?: string;
  to?: string;
  category?: AiAgentCategory;
  tenantId?: string;
  feature?: string;
  agentKey?: string;
  model?: string;
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

interface AiOpsState {
  category: CategoryFilter;
  filters: AiUsageFilters;

  summary: AiUsageSummary | null;
  timeseries: AiUsageTimeseries | null;
  agents: AiAgentConfigDto[];
  pricing: AiModelPricingDto[];
  calls: AiUsageCallRow[];
  callsHasMore: boolean;
  callsPage: number;

  loadingSummary: boolean;
  loadingAgents: boolean;
  loadingPricing: boolean;
  loadingCalls: boolean;
  error: string | null;

  setCategory: (c: CategoryFilter) => void;
  setRange: (from: string, to: string) => void;

  loadOverview: (groupBy?: AiUsageGroupBy) => Promise<void>;
  loadAgents: () => Promise<void>;
  loadPricing: () => Promise<void>;
  loadCalls: (page?: number) => Promise<void>;

  updateAgent: (key: string, patch: UpdateAiAgentConfigInput) => Promise<AiAgentConfigDto | null>;
  resetAgent: (key: string) => Promise<void>;
  syncAgent: (key: string) => Promise<{ ok: boolean; message?: string }>;

  savePricing: (input: UpsertAiModelPricingInput) => Promise<void>;
  patchPricing: (id: string, patch: Partial<UpsertAiModelPricingInput>) => Promise<void>;
  deletePricing: (id: string) => Promise<void>;
}

const E = API_ENDPOINTS.SUPER_ADMIN;

function activeFilters(state: AiOpsState): AiUsageFilters {
  const f = { ...state.filters };
  if (state.category !== 'all') f.category = state.category;
  else delete f.category;
  return f;
}

function isoDaysAgo(days: number): string {
  const d = new Date(Date.now() - days * 86400000);
  return d.toISOString().slice(0, 10);
}

export const useAiOpsStore = create<AiOpsState>((set, get) => ({
  category: 'all',
  filters: { from: isoDaysAgo(29), to: new Date().toISOString().slice(0, 10) },

  summary: null,
  timeseries: null,
  agents: [],
  pricing: [],
  calls: [],
  callsHasMore: false,
  callsPage: 1,

  loadingSummary: false,
  loadingAgents: false,
  loadingPricing: false,
  loadingCalls: false,
  error: null,

  setCategory: (category) => {
    set({ category });
    void get().loadOverview(get().timeseries?.groupBy ?? undefined);
    void get().loadCalls(1);
  },
  setRange: (from, to) => {
    set({ filters: { ...get().filters, from, to } });
    void get().loadOverview(get().timeseries?.groupBy ?? undefined);
    void get().loadCalls(1);
  },

  loadOverview: async (groupBy) => {
    set({ loadingSummary: true, error: null });
    try {
      const api = getApiClient();
      const f = activeFilters(get());
      const base = qs(f as Record<string, string | undefined>);
      const tsQuery = qs({ ...(f as Record<string, string | undefined>), groupBy });
      const [summary, timeseries] = await Promise.all([
        api.get<AiUsageSummary>(`${E.AI_USAGE.SUMMARY}${base}`),
        api.get<AiUsageTimeseries>(`${E.AI_USAGE.TIMESERIES}${tsQuery}`),
      ]);
      set({ summary: summary ?? null, timeseries: timeseries ?? null, loadingSummary: false });
    } catch (err) {
      set({ loadingSummary: false, error: err instanceof Error ? err.message : 'Failed to load AI usage' });
    }
  },

  loadAgents: async () => {
    set({ loadingAgents: true, error: null });
    try {
      const api = getApiClient();
      const cat = get().category !== 'all' ? get().category : undefined;
      const agents = await api.get<AiAgentConfigDto[]>(`${E.AI_CONFIG.AGENTS}${qs({ category: cat })}`);
      set({ agents: agents ?? [], loadingAgents: false });
    } catch (err) {
      set({ loadingAgents: false, error: err instanceof Error ? err.message : 'Failed to load agents' });
    }
  },

  loadPricing: async () => {
    set({ loadingPricing: true });
    try {
      const api = getApiClient();
      const pricing = await api.get<AiModelPricingDto[]>(E.AI_CONFIG.PRICING);
      set({ pricing: pricing ?? [], loadingPricing: false });
    } catch (err) {
      set({ loadingPricing: false, error: err instanceof Error ? err.message : 'Failed to load pricing' });
    }
  },

  loadCalls: async (page = 1) => {
    set({ loadingCalls: true });
    try {
      const api = getApiClient();
      const f = activeFilters(get());
      const limit = 50;
      const url = `${E.AI_USAGE.CALLS}${qs({ ...(f as Record<string, string | undefined>), page: String(page), limit: String(limit) })}`;
      const rows = await api.get<AiUsageCallRow[]>(url);
      set({ calls: rows ?? [], callsPage: page, callsHasMore: (rows?.length ?? 0) >= limit, loadingCalls: false });
    } catch (err) {
      set({ loadingCalls: false, error: err instanceof Error ? err.message : 'Failed to load calls' });
    }
  },

  updateAgent: async (key, patch) => {
    const api = getApiClient();
    const updated = await api.patch<AiAgentConfigDto>(E.AI_CONFIG.AGENT(key), patch);
    if (updated) set({ agents: get().agents.map((a) => (a.key === key ? updated : a)) });
    return updated ?? null;
  },
  resetAgent: async (key) => {
    const api = getApiClient();
    const updated = await api.post<AiAgentConfigDto>(E.AI_CONFIG.AGENT_RESET(key));
    if (updated) set({ agents: get().agents.map((a) => (a.key === key ? updated : a)) });
  },
  syncAgent: async (key) => {
    try {
      const api = getApiClient();
      const updated = await api.post<AiAgentConfigDto>(E.AI_CONFIG.AGENT_SYNC(key));
      if (updated) set({ agents: get().agents.map((a) => (a.key === key ? updated : a)) });
      return { ok: true };
    } catch (err) {
      await get().loadAgents();
      return { ok: false, message: err instanceof Error ? err.message : 'Sync failed' };
    }
  },

  savePricing: async (input) => {
    const api = getApiClient();
    await api.post(E.AI_CONFIG.PRICING, input);
    await get().loadPricing();
  },
  patchPricing: async (id, patch) => {
    const api = getApiClient();
    await api.patch(E.AI_CONFIG.PRICING_ITEM(id), patch);
    await get().loadPricing();
  },
  deletePricing: async (id) => {
    const api = getApiClient();
    await api.delete(E.AI_CONFIG.PRICING_ITEM(id));
    await get().loadPricing();
  },
}));
